<template>
  <div
    class="template-management-list-item w-full flex items-center"
    :class="{ 'bg-primary5': isSelected, '!bg-primary4': isContextMenuOpen || isDraggedOver }"
  >
    <div class="flex items-center">
      <NCheckbox
        :checked="selectedItemIds.includes(item.id as string)"
        class="h-9 w-9 inline-flex items-center justify-center cursor-pointer ml-3"
        :disabled="
          !!launchDarkly.enable_canvas_locks &&
          !isFolder(item) &&
          isCanvasLocked(String(item.id)) &&
          !isLockedByCurrentUser(String(item.id))
        "
        :value="item.id"
        @click.stop="onToggleItemSelection(item)"
      />
      <div v-if="pitcherSettings?.enable_thumbnails_for_templates_and_blocks" class="canvas-thumbnail mr-2">
        <CThumbnail
          class="mr-2"
          :enableThumbnails="true"
          :isFolder="isFolder(item)"
          :item="item as any"
          :themeVars="themeVars"
          :thumbnailUrl="(item as any).thumbnail_url ?? item.metadata?.thumbnail"
        />
      </div>
      <div v-else class="mx-3 my-3">
        <CIcon class="mr-2" v-bind="getIconBindings(item)" />
      </div>
    </div>
    <div class="flex flex-col justify-center gap-1 pa-3 truncate w-full">
      <div class="flex items-center">
        <!-- Lock icon -->
        <CIcon
          v-if="!!launchDarkly.enable_canvas_locks && !isFolder(item) && isCanvasLocked(String(item.id))"
          v-tooltip="getCanvasLockTooltip(String(item.id))"
          class="mr-2"
          :color="isLockedByCurrentUser(String(item.id)) ? themeVars.success : themeVars.warning"
          icon="lock"
          size="16px"
        />
        <CTableInput
          :modelValue="item.name"
          :readonly="
            isReadonly ||
            (!!launchDarkly.enable_canvas_locks &&
              isCanvasLocked(String(item.id)) &&
              !isLockedByCurrentUser(String(item.id)))
          "
          :search="highlightText"
          :state="isEditingCurrentItem(item?.id, 'name') ? 'edit' : 'view'"
          valueClass="font-700 truncate font-size-[18px]"
          @cancel="clearInlineEditingItem"
          @edit="onEdit(item.id, 'name')"
          @update:modelValue="onUpdateName(item.id.toString(), $event)"
        />
      </div>
      <div
        class="truncate font-size-[12px]"
        :style="{ color: isContextMenuOpen || isDraggedOver ? themeVars.text2 : themeVars.text3 }"
      >
        <span v-if="isFolder(item)">
          <CIcon class="mr-1" icon="folder" />
          <span v-if="doNotShowFolderDetails">{{ t('canvasUI.common.folder') }}</span>
          <span v-else>
            {{ getFolderDescription(getItemAsFolder(item), { entity: CoreFolderEntityType.Template }) }}
          </span>
        </span>

        <div v-else-if="item.folder?.name" class="flex gap-2 items-center">
          <div class="flex gap-1 items-center">
            <div>{{ t('canvasUI.files.in') }}</div>
            <div class="color-text3 font-bold">{{ item.folder?.name }}</div>
            <span class="inline-block h-1 w-1 bg-text3 rounded-full"></span>
          </div>
        </div>
        <div v-else class="flex items-center gap-2">
          <CTag
            v-if="!isFolder(item)"
            size="small"
            :style="{
              ...getStatusStyle(),
              border: 'none',
              fontSize: '10px',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              '--n-padding': '4px 8px',
            }"
          >
            {{
              item.is_draft
                ? t('canvasUI.components.templates.CTemplateManagementStatusCell.status.draft')
                : t('canvasUI.components.templates.CTemplateManagementStatusCell.status.ready')
            }}
          </CTag>
          <span v-if="item.is_default" class="flex items-center">
            <CIcon
              class="mr-1"
              color="themeVars.success"
              fa-type="fa-kit"
              icon="solid-presentation-screen-circle-check"
            />
            {{ t('canvasUI.components.CTemplateManagementListItem.defaultTemplate') }}
          </span>
        </div>
      </div>
      <div v-if="item.owned_by?.name" class="truncate text-sm">
        <span class="color-text2 mr-1">{{ t('canvasUI.components.CTemplateManagementListItem.ownedBy') }}:</span>
        <span class="color-text">{{ item.owned_by.name }}</span>
      </div>
      <div class="truncate text-sm">
        <span class="color-text2 mr-1">{{ t('canvasUI.components.CTemplateManagementListItem.modifiedBy') }}:</span>
        <span class="color-text">
          {{ item.modified_by?.name }} {{ t('canvasUI.components.CTemplateManagementListItem.at') }}
          {{ formatDateTime(item.modified_at) }}
        </span>
      </div>
      <CTableTag
        v-if="item?.tags"
        display-as-text
        :maxTagsToShow="0"
        :modelValue="item?.tags"
        :readonly="
          isReadonly ||
          !!item.page ||
          (!!launchDarkly.enable_canvas_locks &&
            isCanvasLocked(String(item.id)) &&
            !isLockedByCurrentUser(String(item.id)))
        "
        :search="highlightText"
        :state="isEditingCurrentItem(item?.id, 'tags') ? 'edit' : 'view'"
        :suggested-tags-fetcher="
          (search) => fetchSuggestedInstanceTags(search, { type: ['canvases'], canvasType: 'template' })
        "
        :tagsClass="'cursor-pointer'"
        @cancel="clearInlineEditingItem"
        @edit="onEdit(item.id, 'tags')"
        @update:modelValue="onUpdateTags(item.id.toString(), $event)"
      />
      <div class="flex flex-wrap gap-2">
        <CContentError v-if="item?.access_type === 'restricted'" only-tag restricted />
        <CPopularityAndEngagementExtended
          v-if="Object.keys(popularityDict)?.length"
          class="gap-2 mr-2"
          :engagement="launchDarkly.show_popularity_icons ? popularityDict[item.id]?.engagement : undefined"
          :is-recommended="!!recommendedSectionIds.includes(item.id?.toString())"
          :popularity="launchDarkly.show_popularity_icons ? popularityDict[item.id]?.popularity : undefined"
          :views="launchDarkly.show_popularity_icons ? (popularityDict[item.id]?.views ?? 0) : 0"
        />
      </div>
    </div>

    <div class="flex justify-end h-full items-center gap-1 cursor-auto py-3 px-4" @click.stop>
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type ComputedRef, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { NCheckbox } from 'naive-ui'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import CTag from '@lib/components/CTag/CTag.vue'
import CPopularityAndEngagementExtended from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementExtended.vue'

import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useThemeVars from '@lib/composables/useThemeVars'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import useCanvasLocks from '@lib/composables/useCanvasLocks'

import { formatDateTime } from '@lib/util/date'

import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { ViewCompactFolder } from '@lib/types/files'
import { CoreFolderEntityType } from '@lib/types/folder'
import type { CanvasRetrieve } from '@openapi'
import type { FontAwesomeType } from '@canvas-builder/types/font-awesome.types'
import tooltipDirective from '@lib/directives/tooltip'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import CContentError from '@lib/components/CContentError/CContentError.vue'

const props = withDefaults(
  defineProps<{
    highlightText?: string
    doNotShowFolderDetails?: boolean
    isContextMenuOpen?: boolean
    isDraggedOver?: boolean
    isSelected?: boolean
    isReadonly?: boolean
    item: { [key: string]: any; id: number | string }
    selectedItemIds: string[]
    popularityDict: Record<string, any>
    recommendedSectionIds: string[]
    isEditingCurrentItem: (id: string | number, prop: 'name' | 'tags') => boolean
  }>(),
  {
    highlightText: '',
    doNotShowFolderDetails: false,
    isContextMenuOpen: false,
    isDraggedOver: false,
    isSelected: false,
    isReadonly: false,
  },
)

const emit = defineEmits<{
  edit: [id: string | number, prop: 'name' | 'tags']
  updateName: [id: string, value: string]
  updateTags: [id: string, value: string[]]
  toggleItemSelection: [item: any, options: { disableMultiple: boolean }]
}>()

const { t } = useI18n()
const { getFolderDescription } = useFileDisplayHelpers()
const themeVars = useThemeVars()
const { fetchSuggestedInstanceTags } = useSuggestedTags()
const vTooltip = tooltipDirective

const { isCanvasLocked, isLockedByCurrentUser, getCanvasLockTooltip } = useCanvasLocks()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({ enable_thumbnails_for_templates_and_blocks: false })),
)

const {
  highlightText,
  doNotShowFolderDetails,
  isContextMenuOpen,
  isSelected,
  isReadonly,
  item,
  selectedItemIds,
  popularityDict,
  recommendedSectionIds,
  isEditingCurrentItem,
} = toRefs(props)

function getItemAsFolder(item: any) {
  return item as ViewCompactFolder
}

function getStatusStyle() {
  return item.value.is_draft
    ? {
        color: themeVars.value.text,
        backgroundColor: themeVars.value.primary6,
      }
    : {
        color: themeVars.value.base,
        backgroundColor: themeVars.value.success,
      }
}

function getIconBindings(item: any) {
  const bindings = {
    color: themeVars.value.primary4,
    faType: 'fas' as FontAwesomeType,
    size: '30px',
  }
  return isFolder(item)
    ? { ...bindings, icon: 'folder' }
    : (item as unknown as CanvasRetrieve).is_default
      ? {
          ...bindings,
          icon: 'solid-presentation-screen-circle-check',
          color: themeVars.value.success,
          faType: 'fa-kit' as FontAwesomeType,
        }
      : { ...bindings, icon: 'presentation-screen', color: themeVars.value.text3, faType: 'far' as FontAwesomeType }
}

function isFolder(item: any) {
  return item?.type === 'folder'
}

function clearInlineEditingItem() {
  emit('edit', '', 'name')
}

function onEdit(id: string | number, prop: 'name' | 'tags') {
  // Prevent editing if the canvas is locked by another user
  if (!isFolder(item.value) && isCanvasLocked(String(id)) && !isLockedByCurrentUser(String(id))) {
    return
  }

  emit('edit', id, prop)
}

function onUpdateName(id: string, value: string) {
  emit('updateName', id, value)
}

function onUpdateTags(id: string, value: string[]) {
  emit('updateTags', id, value)
}

function onToggleItemSelection(item: any, options: { disableMultiple: boolean } = { disableMultiple: false }) {
  emit('toggleItemSelection', item, options)
}
</script>

<style scoped lang="scss">
.template-management-list-item {
  border-bottom: 1px solid v-bind('themeVars.primary6');
}
</style>
